import { z } from "zod";
import { jsonCodec } from "../../../webapp/json-schema";
import {
  type MiscPeriods,
  miscPeriodToCode,
  monthCodeToOffset,
  rowStringToCode,
  rowTypeIsAdhoc,
  type TGridAdhocRow,
  type TGridRow,
} from "@package/grid-utils";

const PeriodTypeSchema = z.enum(["months", "quarters", "halves", "cals", "seasons"]);

type PeriodType = z.infer<typeof PeriodTypeSchema>;

const PeriodValueSchema = z.string();
const UnconfiguredPeriodValueSchema = PeriodValueSchema.optional().nullable();
const PeriodSchema = z.object({
  periodType: PeriodTypeSchema.optional().nullable(),
  periodValue: UnconfiguredPeriodValueSchema,
});

/**
 * This is absolutely horrible - I want to exclude schemas without valid
 * periods, but we don't yet have a branded type or zod schema for representing
 * generalised periods like "Q2 25", "Win 30",
 * but unconfigured time spreads sometimes have period values of "-"
 * when we have a proper representation of valid periods, let's adopt that
 */
const NotDash = z.stringFormat("not-dash", (val) => val !== "-");

export const ValidPeriodValue = NotDash;

const PeriodValueToValidSchema = z.pipe(UnconfiguredPeriodValueSchema, ValidPeriodValue).catch(() => "-");
type ValidPeriodValue = z.infer<typeof PeriodValueToValidSchema>;
/**
 * Combined with the associated schema this tries to get a value or returns "-"
 * It's more of a hack for TimeSpreadsModalWithState than anything else
 * and to be consistent with the period value of "-" seen for unconfigured periods
 */
export const getValidPeriodValueWithDefault = (val: TAdhocPeriod | undefined | null): ValidPeriodValue =>
  PeriodValueToValidSchema.parse(val?.periodValue);

const ValidPeriodSchema = z.object({
  periodType: PeriodTypeSchema,
  periodValue: ValidPeriodValue,
});
const PeriodToValidSchema = z.pipe(PeriodSchema, ValidPeriodSchema);

export const isValidPeriod = (adhocPeriod: TAdhocPeriod): adhocPeriod is TValidAdhocPeriod =>
  PeriodToValidSchema.safeDecode(adhocPeriod).success;

const absurd = (val: never): never => {
  throw new Error(`${val} should never occur`);
};

/**
 * TODO: These conversions are quite awkward:
 * it'd be nice if they were done at the presentation layer
 * and if time spreads used the same period type as everything else
 * please correct me if I'm wrong!
 */
export const rowTypeFromTimespreadPeriodType = (periodType: PeriodType): TGridRow["rowType"] => {
  switch (periodType) {
    case "months":
      return "mth";
    case "quarters":
      return "qtr";
    case "halves":
      return "hlv";
    case "cals":
      return "cal";
    case "seasons":
      return "season";
    default:
      return absurd(periodType);
  }
};

/**
 * TODO: These conversions are quite awkward:
 * it'd be nice if they were done at the presentation layer
 * and if time spreads used the same period type as everything else
 * please correct me if I'm wrong!
 */
export const timespreadPeriodTypeFromRowType = (
  rowType: Exclude<TGridRow["rowType"], TGridAdhocRow["rowType"] | "insert_adhoc">,
): PeriodType => {
  switch (rowType) {
    case "mth":
      return "months";
    case "qtr":
      return "quarters";
    case "hlv":
      return "halves";
    case "cal":
      return "cals";
    case "season":
      return "seasons";
    default:
      return absurd(rowType);
  }
};

/**
 * TODO: These conversions are quite awkward:
 * it'd be nice if they were done at the presentation layer
 * and if time spreads used the same period type as everything else
 * please correct me if I'm wrong!
 *
 * Includes adhoc which isn't represented in PeriodType
 */
export const timespreadPeriodTypeFromRowTypeWithAdhoc = (rowType: TGridRow["rowType"]): PeriodType | "adhoc" => {
  if (rowTypeIsAdhoc(rowType) || rowType === "insert_adhoc") {
    return "adhoc";
  }
  return timespreadPeriodTypeFromRowType(rowType);
};

const SpreadTypeSchema = z.enum(["spread", "strip", "fly", "condor"]);
export type TSpreadType = z.infer<typeof SpreadTypeSchema>;

const SpreadSchema = z.object({
  rowId: z.string(),
  spreadType: SpreadTypeSchema.optional(),
  from: PeriodSchema.optional().nullable(),
  to: PeriodSchema.optional().nullable(),
  middle: PeriodSchema.optional().nullable(),
  middle2: PeriodSchema.optional().nullable(),
  isStrip: z.boolean().optional(),
});
const ValidSpreadSchema = z.object({
  rowId: z.string(),
  spreadType: SpreadTypeSchema,
  from: ValidPeriodSchema,
  to: ValidPeriodSchema,
  middle: ValidPeriodSchema.optional(),
  middle2: ValidPeriodSchema.optional(),
  isStrip: z.boolean().optional(),
});
export const SpreadToValidSchema = z
  .pipe(
    SpreadSchema,
    SpreadSchema.transform((data) => ({
      ...data,
      spreadType: data.spreadType || (data.isStrip ? ("strip" as const) : ("spread" as const)),
    })),
  )
  .pipe(ValidSpreadSchema);

export const isValidSpread = (spread: TAdhocSpread): spread is TValidAdhocSpread =>
  SpreadToValidSchema.safeDecode(spread).success;

export const SpreadJSON = jsonCodec(z.array(SpreadSchema));

export const SpreadRecordFromJSON = z.codec(SpreadJSON, z.record(z.string(), SpreadSchema), {
  decode: (val) => Object.fromEntries(val.map((x) => [x.rowId, x] as const)),
  encode: (val) => Object.entries(val).map(([_, period]) => period),
});

export type TAdhocSpread = z.infer<typeof SpreadSchema>;
export type TAdhocSpreads = Array<TAdhocSpread>;
export type TValidAdhocSpread = z.infer<typeof ValidSpreadSchema>;
export type TValidAdhocSpreads = Array<TValidAdhocSpread>;
export type TAdhocPeriod = z.infer<typeof PeriodSchema>;
export type TValidAdhocPeriod = z.infer<typeof ValidPeriodSchema>;
export type TAdhocSpreadFormatted = Record<string, TAdhocSpread>;
export type TValidAdhocSpreadFormatted = Record<string, TValidAdhocSpread>;

export const getPeriodCode = (period: TAdhocPeriod) => {
  if (!period.periodType || !period.periodValue) return null;
  if (period.periodType === "months") return rowStringToCode(period.periodValue);
  return miscPeriodToCode(period.periodValue as MiscPeriods);
};

export const getPeriodRef = (period: TAdhocPeriod) => {
  if (!period.periodType || !period.periodValue) return null;
  if (period.periodType === "months") {
    const code = rowStringToCode(period.periodValue);
    return monthCodeToOffset(code);
  }
  return miscPeriodToCode(period.periodValue as MiscPeriods);
};
